import {
  AccountListItem,
  AvatarCurrency,
  AvatarProps,
  FormAutocompleteField,
  FormAutocompleteFieldProps,
} from '@razz-team/ui-web';
import { ApiAccount } from '../../openapi';
import { useCallback, useMemo } from 'react';
import { formatServerMoney } from '@razz-team/utils';
import { useTranslation } from 'react-i18next';

export interface AccountFieldProps<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> extends Omit<
  FormAutocompleteFieldProps<string, Multiple, DisableClearable>,
  'options' | 'getOptionLabel'
> {
  accounts: ApiAccount[] | undefined;
  currenciesWhitelist?: string[];
}

export function AccountField<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>({ accounts, currenciesWhitelist, ...props }: AccountFieldProps<Multiple, DisableClearable>) {
  const { t } = useTranslation();
  const options = useMemo(() => accounts?.map((a) => a.id) ?? [], [accounts]);
  const getOptionLabel = useCallback(
    (option: string) => {
      const account = accounts?.find((a) => a.id === option);
      if (!account) {
        return t('Unknown account');
      }

      const accountName = account.name ?? account.balance.currency;
      const balance = formatServerMoney(account.balance);
      return `${accountName} · ${balance}`;
    },
    [accounts, t],
  );
  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const account = accounts?.find((a) => a.id === option);
      return account ? <AccountListItem account={account} {...optionProps} key={option} /> : null;
    },
    [accounts],
  );
  const renderAvatar = useCallback(
    (accountId: string, avatarProps: Partial<AvatarProps>) => {
      const account = accounts?.find((a) => a.id === accountId);
      return account ? (
        <AvatarCurrency currency={account?.balance.currency} {...avatarProps} />
      ) : undefined;
    },
    [accounts],
  );
  const getOptionDisabled = useCallback(
    (option: string) => {
      if (!currenciesWhitelist) {
        return false;
      }
      const account = accounts?.find((a) => a.id === option);
      if (!account) {
        return false;
      }
      return !currenciesWhitelist.includes(account.balance.currency);
    },
    [accounts, currenciesWhitelist],
  );

  return (
    <FormAutocompleteField<string, Multiple, DisableClearable>
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderAvatar={renderAvatar}
      getOptionDisabled={getOptionDisabled}
      {...props}
    />
  );
}
